'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { describeBulkDelete } from '@crm-src/lib/bulk-delete-report';
import { invoicesApi } from "@crm-src/lib/invoices-api";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from 'lucide-react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { PageHeader } from '@/components/ui/page-header';
import { CreateWithAiButton } from '../../../otto-agent/CreateWithAiButton';
import { PageLoader } from '@/components/ui/page-loader';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@crm-src/hooks/useAuth';
import { useUsers } from '@crm-src/hooks/useUsers';
import { useInvoices } from '@crm-src/hooks/useInvoices';
import { useTableQuery } from '@crm-src/components/table/useTableQuery';
import { useDebouncedSearch } from '@crm-src/components/table/useDebouncedSearch';
import { useLatestRequest } from '@crm-src/components/table/useLatestRequest';
import { useTableSelection } from '@crm-src/components/table/useTableSelection';
import { buildTableParams } from '@crm-src/components/table/table-query';
import { TableFilterBar } from '@crm-src/components/table/TableFilterBar';
import { QUOTES_TABLE, type TableColumn } from '@crm-src/components/table/configs';
import { buildInvoiceFacetDefs, type InvoiceFacetKey } from '@crm-src/components/invoice-facets';
import type { InvoiceListFilters } from '@crm-src/lib/store/invoicesSlice';
import { DocumentStatsCards } from "@crm-src/components/document-stats-cards";
import { DocumentListTable } from "@crm-src/components/document-list-table";
import { DocumentBulkActions } from "@crm-src/components/document-bulk-actions";
import { Pagination, paginationPropsFromMeta } from "@/components/ui/pagination";
import { DeleteInvoiceModal } from "@crm-src/components/delete-invoice-modal";
import { BulkDeleteInvoiceModal } from "@crm-src/components/bulk-delete-invoice-modal";
import { ShareDocumentModal } from "@crm-src/components/share-document-modal";
import api from '@crm-src/lib/api';
import type { Invoice } from '@crm-src/lib/store/invoicesSlice';
import { downloadDocumentPdf } from '@crm-src/lib/document-pdf';

interface InvoiceDetails extends Invoice {
  client_invoice_number?: string;
  purchase_order_number?: string;
  client_phone?: string;
  client_address?: string;
  client_company?: string;
  client_tax_id?: string;
  description?: string;
  payment_terms_days?: number;
  payment_terms?: string;
  terms_and_conditions?: string;
  payment_method?: string;
  payment_reference?: string;
  is_recurring?: boolean;
  recurring_frequency?: 'monthly' | 'quarterly' | 'yearly';
  file_name?: string;
  file_type?: string;
  file_size?: number;
  payment_percentage?: number;
  overdue?: boolean;
  archived?: boolean;
  currency?: string;
  discount_amount?: number;
  market_discount?: number;
  reseller_discount?: number;
  additional_discount_amount?: number;
  additional_discount_percentage?: number;
  paid_date?: string;
  subject?: string;
  po_number?: string;
  sales_person?: string;
  terms?: string;
  bill_to_company?: string;
  bill_to_address?: string;
  bill_to_city?: string;
  bill_to_state?: string;
  bill_to_zip?: string;
  bill_to_country?: string;
  ship_to_company?: string;
  ship_to_address?: string;
  ship_to_city?: string;
  ship_to_state?: string;
  ship_to_zip?: string;
  ship_to_country?: string;
  products?: Array<{
    id?: string;
    partNumber?: string;
    description: string;
    seats: number;
    chargePerSeat: number;
    amount: number;
  }>;
  sub_total?: number;
  balance_due?: number;
}

/** Rows per page. Matches every other CRM list. */
const PER_PAGE = 25;

const QuotesPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    invoices,
    isLoading,
    error,
    stats,
    getInvoices,
    removeInvoice,
    removeBulkInvoices,
    getInvoiceStats,
  } = useInvoices();

  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<InvoiceDetails | null>(null);
  const [invoiceToShare, setInvoiceToShare] = useState<InvoiceDetails | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sort / facet / page state. The URL is the SINGLE source of truth — there is
  // deliberately no `useState` copy of any of it, so a refresh, the back button
  // and a pasted link all reproduce the same view. See components/table/.
  //
  // Annotated, not inferred: a bare `: TableConfig` (or an inferred key list)
  // widens `facetKeys` to `string[]`, at which point a misspelled facet key
  // stops being a compile error.
  const facetKeys = useMemo<readonly InvoiceFacetKey[]>(() => QUOTES_TABLE.facetKeys, []);
  const table = useTableQuery({
    defaultSort: QUOTES_TABLE.defaultSort,
    defaultDir: QUOTES_TABLE.defaultDir,
    facetKeys,
  });

  // Only the TEXT box debounces (500ms) — a header or facet click fires at
  // once. The hook also owns the "a new term means page 1 again" reset. Search
  // text is deliberately NOT in the URL; sort, facets and page are.
  const search = useDebouncedSearch(table.setPage);

  const { users } = useUsers();
  // `facetKeys` is passed, not defaulted: `TableFilterBar` renders whatever
  // `facetDefs` it is handed and does not intersect them with the page's
  // declared keys, so the config's list has to reach the BUILDER for a
  // dropped facet to actually disappear. See QUOTES_TABLE.
  const facetDefs = useMemo(() => buildInvoiceFacetDefs(users, facetKeys), [users, facetKeys]);
  // `as const` config entries carry no `align`, so the array needs the explicit
  // element type rather than the literal one TypeScript infers.
  const columns = useMemo<readonly TableColumn[]>(() => QUOTES_TABLE.columns, []);

  // Pagination state (what the LAST response reported — the requested page
  // lives in the URL via `table.page`).
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: PER_PAGE,
    total_count: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });

  const { user } = useAuth();
  const [selectedInvoiceForDetails, setSelectedInvoiceForDetails] = useState<Invoice | null>(null);

  // The exact params the list request sends. Derived, never stored: sort/dir/
  // facets/page come from `table` (i.e. the URL) and the search term from the
  // debounce above. `document_type` is NOT a facet — it selects WHICH page this
  // is — so it rides alongside rather than through `buildTableParams`.
  const listParams = useMemo<InvoiceListFilters>(() => ({
    ...buildTableParams(
      { sort: table.sort, dir: table.dir },
      table.facets,
      table.page,
      PER_PAGE,
      search.search,
      // While a search is running and no column has been picked, this omits
      // `sort`/`dir` so the crm-api ranks by search relevance instead. See
      // buildTableParams.
      table.sortExplicit,
    ),
    document_type: 'quote' as const,
  }), [table.sort, table.dir, table.sortExplicit, table.facets, table.page, search.search]);

  // Bulk selection, cleared whenever `listParams` describes a different result
  // set — otherwise "Delete 3" stays armed against rows a new filter has taken
  // off screen. See useTableSelection.
  const {
    selected: selectedIdList,
    setSelected: setSelectedIdList,
    clear: clearSelection,
  } = useTableSelection<string>(listParams);
  const selectedInvoices = useMemo(() => new Set(selectedIdList), [selectedIdList]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Drops a slow earlier response that would otherwise land after a faster
  // later one — easy to hit now that a header or facet click fires immediately.
  const beginRequest = useLatestRequest();

  const loadQuotes = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      const response = await getInvoices(listParams);
      if (!isCurrent()) return;

      if (response?.pagination) {
        setPagination(prev => ({
          ...prev,
          current_page: response.pagination.current_page,
          total_pages: response.pagination.total_pages,
          total_count: response.pagination.total_count,
          per_page: response.pagination.per_page,
          has_next: response.pagination.has_next || false,
          has_prev: response.pagination.has_prev || false
        }));
      }
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error loading quotes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load quotes',
        variant: 'destructive'
      });
    }
  }, [getInvoices, listParams, beginRequest]);

  // Load quotes on mount and whenever the query (sort / facets / page / search)
  // changes.
  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  useEffect(() => {
    // Load stats on mount - filter for quotes only
    getInvoiceStats({ document_type: 'quote' });
  }, []);

  // Bulk selection functions
  const handleSelectAll = (checked: boolean) => {
    setSelectedIdList(checked && invoices ? invoices.map(invoice => invoice.id.toString()) : []);
  };

  const handleSelectInvoice = (invoiceId: string, checked: boolean) => {
    setSelectedIdList(
      checked
        ? [...selectedIdList, invoiceId]
        : selectedIdList.filter(id => id !== invoiceId),
    );
  };

  const getSelectedInvoiceObjects = (): InvoiceDetails[] => {
    if (!invoices) return [];
    return invoices
      .filter(invoice => selectedInvoices.has(invoice.id.toString()))
      .map(invoice => invoice as InvoiceDetails);
  };

  const handleBulkDelete = async (invoiceIds: string[]) => {
    try {
      setIsBulkDeleting(true);
      
      const numericIds = invoiceIds.map(id => parseInt(id, 10));
      const result = await removeBulkInvoices(numericIds);
      
      // What crm-api actually did — a reseller's mirrored quote is left alone
      // and named, rather than counted as deleted (2026-09-15).
      const report = describeBulkDelete(result.response, numericIds, "quote");
      toast({ title: report.title, description: report.description, ...(report.variant ? { variant: report.variant } : {}) });
      
      clearSelection();
      await loadQuotes();
      await getInvoiceStats();
      
    } catch (error) {
      console.error('Error bulk deleting quotes:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete some or all quotes',
        variant: 'destructive'
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handlePageChange = (page: number) => {
    table.setPage(page);
  };

  const handleConvertToInvoice = async (quote: Invoice) => {
    // A quote addressed TO a reseller with no end customer (a portal request,
    // 2026-09-12) has no customer for the generator to stand on, so crm-api
    // converts it in one call and the new invoice opens. A customer quote
    // still re-opens the generator seeded from the quote.
    const q = quote as Invoice & { bill_to_reseller_id?: number | null };
    if (!quote.customer_id && q.bill_to_reseller_id) {
      try {
        const result = await invoicesApi.convertResellerQuote(quote.id);
        toast({ title: 'Quote converted', description: `Invoice ${result.invoice?.invoice_number ?? ''} was created for the reseller.` });
        navigate(`/crm/invoices/${result.invoice.id}`);
      } catch (error: any) {
        const message = error?.response?.data?.error || error?.message || 'The quote could not be converted.';
        toast({ title: 'Could not convert quote', description: message, variant: 'destructive' });
      }
      return;
    }
    // Navigate to invoice generate page with quoteId to pre-fill data
    navigate(`/crm/invoices/generate?quoteId=${quote.id}`);
  };

  const loadInvoiceDetails = async (id: number) => {
    try {
      const response = await api.get(`/invoices/${id}`);
      const invoiceDetails = response.data.data || response.data.invoice || response.data;
      setSelectedInvoiceForDetails(invoiceDetails);
    } catch (error) {
      console.error('Error loading quote details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load quote details',
        variant: 'destructive'
      });
    }
  };

  // The one generator's PDF, fetched from crm-api — see lib/document-pdf.ts.
  const handleDownloadInvoice = async (invoice: Invoice | InvoiceDetails) => {
    try {
      await downloadDocumentPdf(invoice.id, { documentType: 'quote', number: invoice.invoice_number });
      toast({
        title: 'Success',
        description: 'Quote PDF downloaded successfully'
      });
    } catch (error) {
      console.error('Error downloading quote:', error);
      toast({
        title: 'Error',
        description: 'Failed to download quote PDF',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteInvoiceClick = (invoice: InvoiceDetails) => {
    setInvoiceToDelete(invoice);
    setShowDeleteDialog(true);
  };

  const handleEditInvoiceClick = (invoice: InvoiceDetails) => {
    navigate(`/crm/quotes/generate?invoiceId=${invoice.id}`);
  };

  const handleDuplicateQuoteClick = (quote: InvoiceDetails) => {
    // Navigate to generate page with duplicateFrom parameter
    // This will load all quote data EXCEPT the quote number (which will be auto-generated)
    navigate(`/crm/quotes/generate?duplicateFrom=${quote.id}`);
  };

  const handleDeleteInvoiceConfirm = async () => {
    if (!invoiceToDelete) return;
    
    try {
      setIsDeleting(true);
      await removeInvoice(invoiceToDelete.id);
      
      toast({
        title: 'Success',
        description: 'Quote deleted successfully'
      });
      
      setShowDeleteDialog(false);
      setInvoiceToDelete(null);
      // Refresh stats - filter for quotes only
      getInvoiceStats({ document_type: 'quote' });
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete quote',
        variant: 'destructive'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteModalClose = () => {
    if (isDeleting) return;
    setShowDeleteDialog(false);
    setInvoiceToDelete(null);
  };

  const handleShareQuoteClick = (invoice: InvoiceDetails) => {
    setInvoiceToShare(invoice);
    setShowShareDialog(true);
  };

  const handleShareModalClose = () => {
    setShowShareDialog(false);
    setInvoiceToShare(null);
  };

  if (isLoading && !stats) {
    return <PageLoader />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header */}
      <PageHeader
        title="Quote Management"
        description="Manage and track all your quotes"
        actions={
          <>
            <CreateWithAiButton skill="create_quote" />
            <Button variant="contained" onClick={() => navigate('/crm/quotes/generate')} startIcon={<Plus size={16} />}>
              Create Quote
            </Button>
          </>
        }
      />

      {/* Stats Cards */}
      <DocumentStatsCards stats={stats} documentType="quote" />

      {/* Quotes Table */}
      <Card>
        <CardHeader
          // The header now hosts a whole filter row, not one field: render it
          // as a <div> so the Boxes/Selects inside are not nested in a <span>.
          titleTypographyProps={{ component: "div" }}
          title={
            // The search box is WRAPPED, not replaced — same field, same 500ms
            // debounce; the facets simply sit beside it.
            <TableFilterBar
              facetDefs={facetDefs}
              values={table.facets}
              onChange={table.setFacet}
              onClearAll={table.clearFacets}
              searchSlot={
                <TextField
                  size="small"
                  placeholder="Search by quote number, deal name, client name, company..."
                  value={search.input}
                  onChange={(e) => search.setInput(e.target.value)}
                  sx={{ width: { xs: "100%", sm: 320 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ display: "flex", color: search.isSearching ? "primary.main" : "text.disabled" }}><Search size={16} /></Box>
                      </InputAdornment>
                    ),
                    ...(search.isSearching ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography component="span" sx={{ fontSize: 14, color: "primary.main" }}>Searching...</Typography>
                        </InputAdornment>
                      ),
                    } : {}),
                  }}
                />
              }
            />
          }
        />
        <CardContent>
          {/* Bulk Actions Toolbar */}
          <DocumentBulkActions
            selectedCount={selectedInvoices.size}
            documentType="quote"
            onClearSelection={clearSelection}
            onBulkDelete={() => setShowBulkDeleteDialog(true)}
            isDeleting={isBulkDeleting}
          />
          
          <Box sx={{ overflowX: "auto" }}>
            <DocumentListTable
              documents={invoices || []}
              isLoading={isLoading}
              documentType="quote"
              columns={columns}
              sort={table.sort}
              dir={table.dir}
              onSort={table.toggleSort}
              hasSearch={search.input.trim().length > 0}
              activeFilterCount={table.activeFilterCount}
              onClearFilters={table.clearFacets}
              selectedDocuments={selectedInvoices}
              onSelectAll={handleSelectAll}
              onSelectDocument={handleSelectInvoice}
              onViewDetails={(doc) => {
                navigate(`/crm/quotes/${doc.id}`);
              }}
              onEdit={handleEditInvoiceClick}
              onDuplicate={handleDuplicateQuoteClick}
              onShare={handleShareQuoteClick}
              onDownload={handleDownloadInvoice}
              onDelete={handleDeleteInvoiceClick}
              onConvertToInvoice={handleConvertToInvoice}
            />
          </Box>
          
          {/* Pagination */}
          <Pagination
            {...paginationPropsFromMeta(pagination)}
            entityNoun="quotes"
            hideIfSinglePage
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {/* Quote Details Dialog */}
      <Dialog open={showDetailDialog} onClose={() => setShowDetailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: "center" }}>
          <Typography component="span" sx={{ fontSize: "1.5rem", fontWeight: 700, display: "block" }}>
            {selectedInvoiceForDetails?.invoice_number || 'Quote Details'}
          </Typography>
          <Typography sx={{ fontSize: "1.125rem", color: "text.secondary" }}>
            {selectedInvoiceForDetails?.customer?.company_name || 'N/A'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedInvoiceForDetails && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  sx={{ flex: 1 }}
                  onClick={() => {
                    if (selectedInvoiceForDetails) {
                      handleDownloadInvoice(selectedInvoiceForDetails);
                    }
                  }}
                >
                  Download PDF
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Quote Modal */}
      <DeleteInvoiceModal
        isOpen={showDeleteDialog}
        onClose={handleDeleteModalClose}
        invoiceId={invoiceToDelete?.id?.toString() || null}
        onConfirmDelete={(invoiceId) => handleDeleteInvoiceConfirm()}
        documentType="quote"
      />

      {/* Bulk Delete Quote Modal */}
      <BulkDeleteInvoiceModal
        isOpen={showBulkDeleteDialog}
        onClose={() => setShowBulkDeleteDialog(false)}
        selectedInvoices={getSelectedInvoiceObjects()}
        onConfirmDelete={handleBulkDelete}
        documentType="quote"
      />

      {/* Share Quote Modal */}
      <ShareDocumentModal
        isOpen={showShareDialog}
        onClose={handleShareModalClose}
        documentId={invoiceToShare?.id?.toString() || null}
        documentType="quote"
        documentNumber={invoiceToShare?.invoice_number}
      />
    </Box>
  );
};

export default QuotesPage;

